import { useCallback, useEffect, useRef } from 'react';

import { bind } from 'bind-event-listener';
import { createHook, createSelector, createStore } from 'react-sweet-state';

import {
	type ApiError,
	mapErrorToApiError,
} from '@atlassian/conversation-assistant-service-api/utils/api-error';

type CacheEntry<T = any> = {
	data: T | null;
	error: ApiError | null;
	isFetching: boolean;
};

type QueryState = { cache: Record<string, CacheEntry> };

const store = createStore({
	initialState: {
		cache: {},
	} as QueryState,
	actions: {
		updateCacheEntry:
			(key: string, value: Partial<CacheEntry>) =>
			({ setState, getState }) => {
				const state = getState();

				const currentCacheEntry = state.cache[key] ?? {
					data: null,
					error: null,
					isFetching: false,
				};
				const newCacheEntry = { ...currentCacheEntry, ...value };

				setState({
					cache: {
						...state.cache,
						[key]: newCacheEntry,
					},
				});
			},
		getCacheEntry:
			(key: string) =>
			({ getState }) => {
				const state = getState();
				return state.cache[key];
			},
	},
});

const selector = createSelector(
	(state: QueryState) => state.cache,
	(_, props: { key: string; initialQueryEnabled: boolean }) => props.key,
	(_, props: { key: string; initialQueryEnabled: boolean }) => props.initialQueryEnabled,
	/**
	 * Based on testing, the result of this selector will be compared with previous result using `shallowEqual`
	 * and it will determine whether to rerender the component
	 * and also after looking at docs in https://github.com/atlassian/react-sweet-state/blob/master/src/utils/memoize.js#L38
	 *
	 * e.g. for `enabled` true condition:
	 * -> 1st call -> cache[key] === undefined; so it will return { data: null, error: null, isFetching: true }
	 * -> 2nd call -> cache[key] !== undefined; so it will return cache[key]; however shallowEqual(cache[key], previousResult) will return true -> so no rerender
	 *
	 * To test this, run the unit test, and then force this function to return new stuff on every call
	 * e.g. `return { ...result, random: Math.random() };`
	 * it will result in increase renderCount in the unit test
	 */
	(cache, key, initialQueryEnabled) =>
		cache[key] ?? { data: null, error: null, isFetching: initialQueryEnabled },
);

const useCacheEntry = createHook(store, {
	selector,
});

type UseQueryProps<TResponse> = {
	/**
	 * Requests are cached based on the stringified queryKey.
	 * E.g ["fetchAgents", "agent-1"]
	 */
	queryKey: (string | number)[];
	queryFn: () => Promise<TResponse>;
	onSuccess?: (response: TResponse) => void;
	onError?: (error: ApiError) => void;
	/** Set to false to disable automatically fetching */
	enabled?: boolean;
	refetchOnWindowFocus?: boolean;
};

export const useQuery = <TResponse>({
	queryKey: queryKeyParts,
	queryFn,
	onSuccess,
	onError,
	enabled = true,
	refetchOnWindowFocus = false,
}: UseQueryProps<TResponse>): {
	data: TResponse | null;
	error: ApiError | null;
	isFetching: boolean;
	refetch: () => Promise<TResponse | null>;
	/** Optimistically update the cached data without triggering a refetch */
	updateCache: (data: TResponse) => void;
} => {
	const queryKey = queryKeyParts.map((x) => x?.toString()).join('_');
	const queryFnRef = useRef(queryFn);
	queryFnRef.current = queryFn;

	const onSuccessRef = useRef(onSuccess);
	onSuccessRef.current = onSuccess;

	const onErrorRef = useRef(onError);
	onErrorRef.current = onError;

	const [cache, { updateCacheEntry, getCacheEntry }] = useCacheEntry({
		key: queryKey,
		initialQueryEnabled: enabled,
	});

	const fetchData = useCallback(async () => {
		updateCacheEntry(queryKey, { isFetching: true });

		try {
			const response = await queryFnRef.current();
			updateCacheEntry(queryKey, { data: response, error: null });

			onSuccessRef.current?.(response);
			return response;
		} catch (error: any) {
			const apiError = mapErrorToApiError(error);
			updateCacheEntry(queryKey, { error: apiError });
			onErrorRef.current?.(apiError);
		} finally {
			updateCacheEntry(queryKey, { isFetching: false });
		}

		return null;
	}, [updateCacheEntry, queryKey]);

	const updateCache = useCallback(
		(data: TResponse) => {
			updateCacheEntry(queryKey, { data });
		},
		[updateCacheEntry, queryKey],
	);

	useEffect(() => {
		if (enabled && refetchOnWindowFocus) {
			const unbind = bind(window, {
				// We use visibilitychange instead of focus to prevent triggering refetch when moving between iframes such as agent embed
				type: 'visibilitychange',
				listener: () => {
					if (enabled && refetchOnWindowFocus && !getCacheEntry(queryKey)?.isFetching) {
						if (document.visibilityState === 'visible') {
							fetchData();
						}
					}
				},
			});

			return unbind;
		}
	}, [enabled, fetchData, getCacheEntry, queryKey, refetchOnWindowFocus]);

	useEffect(() => {
		if (!getCacheEntry(queryKey) && enabled) {
			fetchData();
		}
	}, [queryKey, fetchData, enabled, getCacheEntry]);

	return {
		data: cache.data,
		error: cache.error,
		isFetching: cache.isFetching,
		refetch: fetchData,
		updateCache,
	};
};
